// ============================================
// FILE: /frontend/src/components/whatsapp/ChatOnWhatsAppButton.jsx
// PURPOSE: WhatsApp chat button component (for contacting seller)
// ============================================

import { useState } from 'react';
import { useAuth } from '../../hooks/useAuth';
import WhatsAppSafetyModal from '../modals/WhatsAppSafetyModal';
import { useToast } from '../../hooks/useToast';
import { useNavigate } from 'react-router-dom';
import { whatsappAPI } from '../../api/whatsapp.api';
import { Button } from '../ui/button';
import { MessageCircle, Loader2 } from 'lucide-react';

/**
 * ChatOnWhatsAppButton Component
 * Opens WhatsApp chat with seller directly
 * 
 * @param {Object} props
 * @param {string} props.listingId - Listing ID
 * @param {string} props.listingTitle - Listing title for display
 * @param {'icon' | 'button' | 'full'} props.variant - Button style variant
 * @param {'sm' | 'md' | 'lg'} props.size - Button size
 * @param {string} props.className - Additional CSS classes
 */
export default function ChatOnWhatsAppButton({ 
  listingId,
  listingTitle,
  variant = 'button',
  size = 'md',
  className = ''
}) {
  console.log('[ChatOnWhatsApp] 🎨 Rendering button for listing:', listingId);

  const { user } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [showSafetyModal, setShowSafetyModal] = useState(false);

  // ========================================
  // HANDLE CHAT CLICK
  // ========================================
  const handleChat = async (e) => {
    e.preventDefault();
    e.stopPropagation();

    console.log('[ChatOnWhatsApp] 💬 Chat button clicked');

    // Show safety modal first
    console.log('[ChatOnWhatsApp] 🛡️ Showing safety modal');
    setShowSafetyModal(true);
  };

  // ========================================
  // HANDLE PROCEED TO WHATSAPP
  // ========================================
  const handleProceedToWhatsApp = async () => {
    console.log('[ChatOnWhatsApp] ✅ User acknowledged safety - proceeding');
    setShowSafetyModal(false);


    setIsLoading(true);

    try {
      // Generate chat inquiry message
      console.log('[ChatOnWhatsApp] 🔵 Generating chat message...');
      const response = await whatsappAPI.generateChatMessage(listingId);

      console.log('[ChatOnWhatsApp] ✅ Chat message generated:', {
        trackingId: response.trackingId,
        sellerName: response.sellerName
      });

      // Show success toast
      toast({
        title: 'Opening WhatsApp',
        description: `Chat with ${response.sellerName || 'seller'}`
      });

      // Open WhatsApp chat with seller
      window.open(response.whatsappUrl, '_blank');

      console.log('[ChatOnWhatsApp] 📱 WhatsApp chat opened');

    } catch (error) {
      console.error('[ChatOnWhatsApp] ❌ Chat error:', error);
      
      const errorMessage = error.response?.data?.message || 'Could not open WhatsApp chat';
      
      toast({
        variant: 'destructive',
        title: 'Chat failed',
        description: errorMessage
      });
    } finally {
      setIsLoading(false);
    }
  };

  // ========================================
  // RENDER VARIANTS
  // ========================================
  
  // Icon only variant
  if (variant === 'icon') {
    return (
      <Button
        variant="ghost"
        size="icon"
        onClick={handleChat}
        disabled={isLoading}
        className={`text-green-600 hover:text-green-700 hover:bg-green-50 ${className}`}
        title="Chat on WhatsApp"
      >
        {isLoading ? (
          <Loader2 className="w-4 h-4 sm:w-5 sm:h-5 animate-spin" />
        ) : (
          <MessageCircle className="w-4 h-4 sm:w-5 sm:h-5" />
        )}
      </Button>
    );
  }

  // Full width button variant
  if (variant === 'full') {
    return (
      <>
        <Button
          onClick={handleChat}
          disabled={isLoading}
          className={`w-full bg-green-600 hover:bg-green-700 text-white h-10 sm:h-11 text-sm sm:text-base font-medium ${className}`}
        >
          {isLoading ? (
            <>
              <Loader2 className="w-5 h-5 mr-2 animate-spin" />
              Opening WhatsApp...
            </>
          ) : (
            <>
              <MessageCircle className="w-5 h-5 mr-2" />
              Chat on WhatsApp
            </>
          )}
        </Button>

        <WhatsAppSafetyModal
          isOpen={showSafetyModal}
          onClose={() => setShowSafetyModal(false)}
          onProceed={handleProceedToWhatsApp}
          sellerName={listingTitle}
        />
      </>
    );
  }

  // Default button variant
  const sizeClasses = {
    sm: 'h-8 text-xs px-2',
    md: 'h-9 sm:h-10 text-xs sm:text-sm px-3',
    lg: 'h-10 sm:h-11 text-sm sm:text-base px-4'
  };

  return (
    <>
      <Button
        onClick={handleChat}
        disabled={isLoading}
        className={`bg-green-600 hover:bg-green-700 text-white ${sizeClasses[size]} font-medium ${className}`}
      >
        {isLoading ? (
          <>
            <Loader2 className="w-4 h-4 sm:w-5 sm:h-5 mr-1 sm:mr-2 animate-spin" />
            Loading...
          </>
        ) : (
          <>
            <MessageCircle className="w-4 h-4 sm:w-5 sm:h-5 mr-1 sm:mr-2" />
            Chat on WhatsApp
          </>
        )}
      </Button>

      <WhatsAppSafetyModal
        isOpen={showSafetyModal}
        onClose={() => setShowSafetyModal(false)}
        onProceed={handleProceedToWhatsApp}
        sellerName={listingTitle}
      />
    </>
  );
}